/**
 * CritiqueCommandWell Component
 *
 * Extensible toolbar for critique enhancement commands.
 * Contains "Suggest Edit" and "Markup" buttons.
 *
 * Feature 071: AI critique feedback moved to session-based system.
 * The "+ From AI" button has been removed in favor of dedicated AI critique sessions.
 *
 * Future commands (out of scope for MVP):
 * - Attach reference images
 * - Add comparison photo links
 * - Record voice critique notes
 * - Attach technical diagrams
 * - Link to tutorial resources
 *
 * Feature: 043-critique-edit-suggestions
 * Feature: 050-critique-markup-integration
 */

import React from 'react';
import { Button } from '@/components/atoms/Button';
import { CritiqueCommandWellProps } from './CritiqueCommandWell.types';

export const CritiqueCommandWell: React.FC<CritiqueCommandWellProps> = ({
  isEditingSuggestion,
  onToggleSuggestEdit,
  isEditingMarkup,
  onToggleMarkup,
  disabled = false,
  className = '',
}) => {
  return (
    <div
      className={`flex items-center gap-2 p-2 border-b border-border bg-surface-1 ${className}`}
      role="toolbar"
      aria-label="Critique enhancement commands"
    >
      <Button
        type="button"
        onClick={onToggleSuggestEdit}
        disabled={disabled}
        variant='secondary'
        size="sm"
        aria-pressed={isEditingSuggestion}
        aria-label={
          isEditingSuggestion
            ? 'Exit edit suggestion mode'
            : 'Suggest edit for this photo'
        }
      >
        {isEditingSuggestion ? '✓ Edit' : '+ Edit'}
      </Button>

      <Button
        type="button"
        onClick={onToggleMarkup}
        disabled={disabled}
        variant='secondary'
        size="sm"
        aria-pressed={isEditingMarkup}
        aria-label={
          isEditingMarkup
            ? 'Exit markup annotation mode'
            : 'Add markup annotation to this photo'
        }
      >
        {isEditingMarkup ? '✓ Markup' : '+ Markup'}
      </Button>

      {/* Future commands will be added here */}
      {/* Example: <button>+ Attach Reference</button> */}
    </div>
  );
};

CritiqueCommandWell.displayName = 'CritiqueCommandWell';
